import React from 'react';
import * as S from './button-group.styles';

export interface IButtonGroupItemProps {
    label: string;
    disabled?: boolean;
    /**
     * Why the item is unavailable, shown on hover. A disabled button shows no title of its own, so an item that
     * has one is wrapped in an element that can.
     */
    title?: string;
}

export interface IButtonGroupProps {
    className?: string;
    buttons: IButtonGroupItemProps[];
    selected?: IButtonGroupItemProps;
    onSelect?: (selected: IButtonGroupItemProps) => void;
    /**
     * `segmented` (default) joins the buttons into one bar with a grey selected segment. `pills` spaces them
     * apart and marks the selected one in blue — the Group by control beside the denials table. `track` sets
     * small segments in one grey track and lifts the selected one onto white — the date range control above it.
     * `segmented` stays the default, so existing callers keep their look.
     */
    variant?: S.ButtonGroupVariant;
}

interface IButtonGroupState {
    selected: IButtonGroupItemProps;
}

export class ButtonGroup extends React.PureComponent<IButtonGroupProps, IButtonGroupState> {
    constructor(props: IButtonGroupProps) {
        super(props);
        this.state = {
            selected: this.props.selected || this.props.buttons[0],
        };
    }
    public componentDidUpdate(): void {
        if (this.props.selected && this.props.selected !== this.state.selected) {
            this.setState({
                selected: this.props.selected,
            });
        }
    }
    public render(): React.ReactElement {
        return (
            <S.Container className={this.props.className} variant={this.props.variant}>
                {this.renderButtons()}
            </S.Container>
        );
    }
    private readonly renderButtons = (): JSX.Element[] => {
        return this.props.buttons.map((button: IButtonGroupItemProps) => {
            const isSelected = this.state.selected.label === button.label;
            const buttonProps = {
                onClick: this.changeSelected(button),
                disabled: button.disabled,
                selected: isSelected,
                // Which segment is current, for a screen reader: the selected styling alone announces nothing.
                'aria-pressed': isSelected,
            };
            const element =
                this.props.variant === 'pills' ? (
                    <S.Pill key={button.label} {...buttonProps}>
                        {button.label}
                    </S.Pill>
                ) : this.props.variant === 'track' ? (
                    <S.TrackItem key={button.label} {...buttonProps}>
                        {button.label}
                    </S.TrackItem>
                ) : (
                    <S.Button key={button.label} {...buttonProps}>
                        {button.label}
                    </S.Button>
                );

            return button.title ? (
                <S.ItemSlot key={button.label} title={button.title}>
                    {element}
                </S.ItemSlot>
            ) : (
                element
            );
        });
    };

    private readonly changeSelected = (selected: IButtonGroupItemProps): (() => void) => {
        return (): void => {
            if (selected !== this.state.selected) {
                this.setState({
                    selected,
                });
                if (this.props.onSelect) {
                    this.props.onSelect(selected);
                }
            }
        };
    };
}
